/* useDisplayPreferences.js
   ------------------------
   Sitewide visual / accessibility preferences:
     - skinTone:     "original" | "medium" | "deep"
     - leftHanded:   boolean    (mirrors hands for left-handed signers)
     - textScale:    1 | 1.1 | 1.25 | 1.5   (root font-size multiplier)
     - highContrast: boolean    (swaps to a stronger contrast palette)
     - reduceMotion: boolean    (forces motion to a minimum)

   Persisted to localStorage so the choice is remembered across pages
   and sessions. Used by FingerspellPlayer, FingerspellImage,
   FingerspellQuiz, Sprout, and applied site-wide via
   `useApplyDisplayPreferences` mounted at the App root.
*/

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "bsl_display_prefs_v1";
const SKIN_TONES = ["original", "medium", "deep"];
const TEXT_SCALES = [1, 1.1, 1.25, 1.5];
const DEFAULTS = {
  skinTone: "original",
  leftHanded: false,
  textScale: 1,
  highContrast: false,
  reduceMotion: false,
};

// Tolerate older saved values - we previously labelled the deepest swatch
// "dark", so any existing local-storage entries are migrated on read.
const normaliseTone = (raw) => {
  if (raw === "dark") return "deep";
  return SKIN_TONES.includes(raw) ? raw : DEFAULTS.skinTone;
};
const normaliseScale = (raw) => {
  const n = Number(raw);
  return TEXT_SCALES.includes(n) ? n : DEFAULTS.textScale;
};

const readFromStorage = () => {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      skinTone: normaliseTone(parsed.skinTone),
      leftHanded: typeof parsed.leftHanded === "boolean" ? parsed.leftHanded : DEFAULTS.leftHanded,
      textScale: normaliseScale(parsed.textScale),
      highContrast: typeof parsed.highContrast === "boolean" ? parsed.highContrast : DEFAULTS.highContrast,
      reduceMotion: typeof parsed.reduceMotion === "boolean" ? parsed.reduceMotion : DEFAULTS.reduceMotion,
    };
  } catch (e) {
    return DEFAULTS;
  }
};

const writeToStorage = (prefs) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    /* ignore quota / disabled storage */
  }
};

// Cross-instance sync: when one component updates the prefs, all
// mounted instances re-read so the choice is reflected everywhere
// without prop drilling.
const PREFS_EVENT = "bsl-display-prefs-changed";

export const useDisplayPreferences = () => {
  const [prefs, setPrefs] = useState(readFromStorage);

  useEffect(() => {
    const onChange = () => setPrefs(readFromStorage());
    window.addEventListener(PREFS_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(PREFS_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const update = useCallback((patch) => {
    const next = { ...readFromStorage(), ...patch };
    writeToStorage(next);
    setPrefs(next);
    window.dispatchEvent(new CustomEvent(PREFS_EVENT));
  }, []);

  return {
    skinTone: prefs.skinTone,
    leftHanded: prefs.leftHanded,
    textScale: prefs.textScale,
    highContrast: prefs.highContrast,
    reduceMotion: prefs.reduceMotion,
    setSkinTone: (t) => update({ skinTone: normaliseTone(t) }),
    setLeftHanded: (b) => update({ leftHanded: !!b }),
    setTextScale: (n) => update({ textScale: normaliseScale(n) }),
    setHighContrast: (b) => update({ highContrast: !!b }),
    setReduceMotion: (b) => update({ reduceMotion: !!b }),
    /* Reset all prefs back to defaults (used by the "reset" button
       in the inline panel). */
    resetPrefs: () => update({ ...DEFAULTS }),
  };
};

/* Mount this once at the App root. Writes the current preferences
   onto <html> as data-* attributes / CSS custom properties so the
   global CSS rules (in content-pages.css and App.css) can react.
   Reading from the hook keeps the side-effect synced across tabs. */
export const useApplyDisplayPreferences = () => {
  const { textScale, highContrast, reduceMotion } = useDisplayPreferences();
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    html.style.setProperty("--bs-text-scale", String(textScale));
    html.setAttribute("data-text-scale", String(textScale));
    html.toggleAttribute("data-high-contrast", highContrast);
    html.toggleAttribute("data-reduce-motion", reduceMotion);
  }, [textScale, highContrast, reduceMotion]);
};

import { cachedAlphabetUrl } from "../lib/useAlphabetPreload";

export const getAlphabetSrc = (letter, skinTone = "original") => {
  if (!letter) return "";
  const safe = String(letter).toLowerCase();
  const tone = normaliseTone(skinTone);
  let url;
  if (tone === "medium") url = `/images/alphabet/medium/${safe}.png`;
  else if (tone === "deep") url = `/images/alphabet/deep/${safe}.png`;
  else url = `/images/alphabet/${safe}.png`;
  /* If the alphabet has been preloaded by `useAlphabetPreload`, this
     returns a local `blob:` URL that paints with no network /
     decode latency; otherwise it returns the original URL unchanged
     and the browser handles the fetch normally. Safe on every code
     path. */
  return cachedAlphabetUrl(url);
};

export const ALL_SKIN_TONES = SKIN_TONES;
export const ALL_TEXT_SCALES = TEXT_SCALES;
